import { along as turfAlong } from '@turf/along';
import { bbox as turfBbox } from '@turf/bbox';
import { length as turfLength } from '@turf/length';
import qs from 'qs';
import * as R from 'ramda';

import {
  GeoJSONGeometry,
  GeoJSONPoint,
  LocationGeometry,
} from '@atom/types/geojson';

import { GOOGLE_MAPS_API_KEY } from './mapConfig';

export const zipCoordinatePair = R.zipObj(['lng', 'lat']);

export const isPoint = (
  geometry: GeoJSONGeometry | null | undefined,
): geometry is GeoJSONPoint => geometry?.type === 'Point';

const COORDINATE_REGEX =
  /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

export const parseCoordinates = (
  text: string,
): google.maps.LatLngLiteral | null => {
  if (!COORDINATE_REGEX.test(text)) {
    return null;
  }
  const [latStr, lngStr] = text.split(',');
  return { lat: parseFloat(latStr), lng: parseFloat(lngStr) };
};

export const getGeoJSONLocation = (location: {
  lat: number;
  lng: number;
}): GeoJSONPoint => ({
  type: 'Point',
  coordinates: [location?.lng ?? 0, location?.lat ?? 0],
});

export const formatCoordinates = (
  { lat, lng }: google.maps.LatLngLiteral,
  decimalPlaces: number,
): string => `${lat.toFixed(decimalPlaces)}, ${lng.toFixed(decimalPlaces)}`;

export const generatePath = (coordinates: number[][]) =>
  coordinates.map(zipCoordinatePair);

export const getLineStringCenter = (shape: {
  type: 'LineString';
  coordinates: number[][];
}) => {
  const line = {
    type: 'Feature' as const,
    properties: {},
    geometry: shape,
  };

  const lengthToCenter = turfLength(line) / 2;
  const center = turfAlong(line, lengthToCenter);

  return center;
};

export const getGoogleShapeCenter = (
  shape: GeoJSONGeometry,
): google.maps.LatLngLiteral => {
  if (R.isNil(shape) || R.isEmpty(shape)) {
    // Return a generic center
    return { lat: 0, lng: 0 };
  }

  if (shape.type === 'Point') {
    const coordinates = zipCoordinatePair(shape.coordinates);
    return {
      lat: Number(coordinates.lat),
      lng: Number(coordinates.lng),
    };
  }

  if (shape.type === 'LineString') {
    const coordinates = zipCoordinatePair(
      getLineStringCenter(shape).geometry.coordinates,
    );
    return {
      lat: Number(coordinates.lat),
      lng: Number(coordinates.lng),
    };
  }

  // Return a generic center
  return { lat: 0, lng: 0 };
};

export const trimPointLatLng = (
  geometry: LocationGeometry,
  decimalPlaces: number,
): LocationGeometry => {
  const round = (value: number): number => Number(value.toFixed(decimalPlaces));
  const coordinates =
    geometry.type === 'LineString'
      ? geometry.coordinates.map(position => position.map(round))
      : geometry.coordinates.map(round);
  return { ...geometry, coordinates } as LocationGeometry;
};

export const getStaticImage = (
  entityLocation: GeoJSONGeometry,
  size = '340x160',
): string => {
  const center = getGoogleShapeCenter(entityLocation);

  const params = qs.stringify({
    center: `${center.lat},${center.lng}`,
    zoom: 18,
    size,
    maptype: 'satellite',
    format: 'png',
    visual_refresh: true,
    key: GOOGLE_MAPS_API_KEY,
  });

  return `https://maps.googleapis.com/maps/api/staticmap?${params}`;
};

export const getFitBoundsFromShape = (
  shape: GeoJSONGeometry,
): number[] | null => {
  if (R.isNil(shape) || R.isEmpty(shape)) {
    return null;
  }

  return turfBbox(shape);
};

export const getGeoJsonFromMapBounds = (map: google.maps.Map) => {
  const bounds = map.getBounds().toJSON();
  const northwest = [bounds.west, bounds.north];
  const northeast = [bounds.east, bounds.north];
  const southeast = [bounds.east, bounds.south];
  const southwest = [bounds.west, bounds.south];
  return {
    type: 'Polygon' as const,
    coordinates: [[northeast, northwest, southwest, southeast, northeast]],
  };
};

export const boundsToCoordinates = R.pipe(
  getGeoJsonFromMapBounds,
  R.prop('coordinates'),
  JSON.stringify,
);

export const urlValueToLatLng = (
  urlValue: string,
): google.maps.LatLngLiteral => {
  const [lat, lng] = urlValue.split(',').map(Number);
  return { lat, lng };
};

export const latLngToUrlValue = ({
  lat,
  lng,
}: google.maps.LatLngLiteral): string =>
  `${Number(lat.toFixed(6))},${Number(lng.toFixed(6))}`;

export const getCurrentUserLocation = (): Promise<{
  lat: number;
  lng: number;
} | null> =>
  new Promise(resolve => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const lat = R.pathOr(0, ['coords', 'latitude'])(position);
        const lng = R.pathOr(0, ['coords', 'longitude'])(position);
        resolve({ lat, lng });
      },
      () => resolve(null),
    );
  });
